import React, { useMemo, useRef } from 'react';

import { Tabs, TabsVariant } from '@lux/tabs';
import { TranslateScope } from '@pegasus/translation';
import { ErrorBoundary } from '@browsing/components';

import { ContinueShoppingData, CardItem } from './types';
import { prepareContinueShoppingData } from './helpers';
import { CategoryTabs, CategoryPanel } from './components';
import { Title } from './components/title';

import * as S from './components/styles';

interface ContinueShoppingCarouselProps {
    data: ContinueShoppingData;
}

const ContinueShoppingCarouselInternal: React.FC<ContinueShoppingCarouselProps> = ({ data }) => {
    const { panelData } = useMemo(() => prepareContinueShoppingData(data), [data]);
    const containerRef = useRef<HTMLDivElement>(null);

    // Early return for empty data to prevent crashes
    if (panelData.length === 0) {
        return null;
    }

    const firstCategory = panelData[0].category;

    const handleClick = (article: CardItem, index: number) => {
        // TODO: Add tracking logic here
        // Navigation is now handled by href attribute in ArticleCompactCard
        console.log('Article clicked for tracking:', { article: article.sku, index });
    };

    const handleSeeAllClick = (categoryId: number) => {
        // TODO: Add tracking logic here
        // Navigation is now handled by href attribute in SeeAllCard
        console.log('See all clicked for tracking:', { categoryId });
    };

    const handleTabChange = (_params: { index: number; value: string }) => {
        // Get category ID for tracking from the panelData using the index
        if (_params.index < panelData.length) {
            // TODO: add tracking
            const _selectedCategory = panelData[_params.index]?.category;
        }
    };

    const handleSwipe = () => {};

    return (
        <S.ContinueShoppingContainer ref={containerRef}>
            <TranslateScope path="continueShopping">
                <Title
                    isSingleCategory={panelData.length === 1}
                    firstCategoryName={firstCategory.category_name}
                />

                <Tabs
                    initialTab={firstCategory.category_id.toString()}
                    variant={TabsVariant.Segmented}
                >
                    {panelData.length > 1 && (
                        <CategoryTabs
                            categories={panelData.map(item => item.category)}
                            onTabChange={handleTabChange}
                        />
                    )}

                    {panelData.map(({ category, shouldShowSeeAll, categoriesList }) => (
                        <CategoryPanel
                            key={category.category_id}
                            category={category}
                            categoriesList={categoriesList}
                            shouldShowSeeAll={shouldShowSeeAll}
                            onArticleClick={handleClick}
                            onSeeAllClick={handleSeeAllClick}
                            onScroll={handleSwipe}
                        />
                    ))}
                </Tabs>
            </TranslateScope>
        </S.ContinueShoppingContainer>
    );
};

export const ContinueShoppingCarousel: React.FC<ContinueShoppingCarouselProps> = props => (
    <ErrorBoundary componentName="ContinueShoppingCarousel">
        <ContinueShoppingCarouselInternal {...props} />
    </ErrorBoundary>
);
